import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

import { celsius_to_fahrenheit, real_id, tmp_units_from_settings } from 'src/helpers/interface_helpers';
import { capFirstChar } from 'src/helpers/utilities';
import { CAMERA_ACTION_NAMES } from 'src/components/Scenes/helpers/camera_action_helpers';
import { DEFAULT_SOUND_ICON, getCameraSoundLabel } from 'src/components/Camera/helpers/camera-sounds';
import { getCameraZoneName } from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';

import { devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';

export function dev_action_to_label_list(action: any = { name: '', value: '' }, device_id) {
	const { name, value } = action;
	const settings = get(devices_settings);

	const _format = get(format);

	switch (name) {
		case 'turn':
		case 'state':
		case 'turn_add_on':
			const isOn = value === 'on';
			const isOff = value === 'off';
			const hasColor = isOn || isOff;

			return {
				name: 'turn',
				icon: isOff
					? 'fa fa-toggle-off'
					: hasColor
						? 'fa-duotone fa-light fa-toggle-on'
						: 'fa fa-toggle-on',
				...(hasColor &&
					isOn && {
						iconStyle:
							'--fa-primary-color: var(--color-success); --fa-secondary-color: var(--color-success-10); --fa-secondary-opacity: 1',
					}),
				...(isOff && {
					iconColor: '--color-danger',
				}),
				value: _format(`scene_action_to_label_${value}`),
			};
		case 'roller state':
		case 'roller direction':
		case 'multi_cover_state':
			return {
				name: 'control_blinds',
				icon:
					value === 'open'
						? 'fa fa-blinds-raised'
						: value === 'close'
							? 'fa fa-blinds'
							: value === 'stop'
								? 'fa fa-hand'
								: false,
				iconColor:
					value === 'open'
						? '--color-primary'
						: value === 'close'
							? '--color-white'
							: value === 'stop'
								? '--color-danger'
								: null,
				value: _format(`execute_${value}`, { default: value }),
			};
		case 'roller position':
		case 'multi_cover_position':
			return {
				name: 'control_blinds_percent',
				icon: 'fa fa-blinds',
				value: `${value}%`,
			};
		case 'blinds_tilt_position':
		case 'multi_cover_blinds_tilt_position':
			return {
				name: 'control_blinds_tilt_percent',
				icon: 'fa fa-arrows-up-down-left-right',
				value: `${value}%`,
			};
		case 'brightness':
			return {
				name: 'color',
				icon: 'fa-light fa-sun-bright',
				value: `${value}%`,
			};
		case 'white_channel':
			return {
				name: 'white_channel',
				icon: 'fa fa-brightness',
				value: `${value}%`,
			};
		case 'temp':
		case 'ct':
			return {
				icon: 'fa fa-circle',
				name: 'color_temp',
				value: `${value} K`,
			};
		case 'mode':
		case 'r':
		case 'g':
		case 'b':
			return '';
		case 'target_temp':
		case 'set_temp': //this is a new action for the wall display's thermostat
			const units = settings[real_id(device_id)]?.thermostats?.[0]?.target_t?.units || 'C';

			return {
				name,
				icon: 'fa fa-temperature-three-quarters',
				value:
					units === 'C'
						? `${value} °${units}`
						: `${celsius_to_fahrenheit(Number(value))} °${units}`,
			};
		case 'temp_enable':
			//this is a new action for the wall display's thermostat
			return {
				name,
				icon: value ? 'fa-duotone fa-light fa-toggle-on' : 'fa fa-toggle-off',
				value: value ? _format('label_action_turn_on') : _format('label_action_turn_off'),
				...(value && {
					iconStyle:
						'--fa-primary-color: var(--color-success); --fa-secondary-color: var(--color-success-10); --fa-secondary-opacity: 1',
				}),
				...(!value && {
					iconColor: '--color-danger',
				}),
			};
		case 'set_profile':
			let profile_name =
				settings[real_id(device_id)]?.thermostats?.[0]?.schedule_profile_names[
					Number(value) - 1
				] || 'Profile ' + value;

			return {
				name,
				icon: '',
				value: capFirstChar(
					_format('command_apply_profile', {
						values: { name: profile_name },
					}),
				),
			};
		case 'report_window_open':
			let value_report_window =
				value === 'on'
					? _format('action_to_label_report_open_window', {
							default: 'report open window',
						})
					: _format('action_to_label_report_closed_window', {
							default: 'report closed window',
						});
			return {
				name,
				icon: 'fa fa-door-open',
				value: value_report_window,
			};
		case 'boost':
			return {
				name,
				icon: '',
				value:
					value === 'on'
						? _format('action_to_label_start_boost', {
								default: 'Start boost',
							})
						: '',
			};
		case 'outputLimit':
			return {
				name,
				icon: 'fa fa-car-battery',
				value: `${value} W`,
			};
		case 'trv_target_temperature':
			const units_blu_trv = tmp_units_from_settings(get(devices)[device_id]);
			// getTrvComponentByDevice(get(devices)[device_id])?.config?.sys?.ui
			// 	.t_units || 'C';
			const converted_value =
				units_blu_trv === '°C'
					? `${value} ${units_blu_trv}`
					: `${celsius_to_fahrenheit(Number(value))} ${units_blu_trv}`;
			return {
				name,
				icon: 'fa fa-temperature-three-quarters',
				value:
					_format('option_temperature_change', {
						default: 'temperature change',
					}) +
					' ' +
					converted_value,
			};
		case 'valve_percent_position':
			return {
				name,
				icon: `fa${Number(value) > 0 ? 's' : 'r'} fa-pipe-valve`,
				value: `${value}%`,
			};
		case 'transition_duration':
			return {
				name,
				icon: 'fa-solid fa-clock-rotate-left',
				value: `${value} ${
					+value === 1
						? _format('select_units_second', { default: 'second' })
						: _format('select_units_seconds', { default: 'seconds' })
				}`,
			};
		case 'bolt_state':
			return {
				name,
				icon:
					value === 'night_lock'
						? 'fa fa-lock'
						: value === 'day_lock'
							? 'fa fa-unlock'
							: 'fa fa-door-open',
				iconColor:
					value === 'night_lock'
						? '--color-danger'
						: value === 'day_lock'
							? '--color-success'
							: '--color-primary',
				value: _format(`label_action_bolt_state_${value}`, {
					default: value,
				}),
			};
		case 'fan_mode':
			return {
				name,
				icon: value === 'auto' ? 'fas fa-wand-magic-sparkles' : 'fas fa-hand',
				value: _format(value === 'auto' ? 'ventora_run_mode_auto' : 'ventora_run_mode_manual', {
					default: value === 'auto' ? 'Auto' : 'Manual',
				}),
			};
		case 'fan_on':
			return {
				name,
				icon: value === 'on' ? 'fas fa-power-off' : 'fas fa-power-off',
				iconColor: value === 'on' ? '--color-success' : '--color-danger',
				value: _format(`scene_action_to_label_${value}`),
			};
		case 'fan_speed':
			return {
				name,
				icon: 'fas fa-tachometer-alt',
				value: `${value}%`,
			};
		case 'MP_Play':
			return {
				name,
				icon: 'fas fa-play',
				iconColor: '--color-success',
				value: _format('label_media_play_song', {
					default: 'Play media song',
				}),
			};
		case 'MP_Stop':
			return {
				name,
				icon: 'fas fa-stop',
				iconColor: '--color-danger',
				value: _format('label_media_stop', {
					default: 'Stop media',
				}),
			};
		case 'MP_Pause':
			return {
				name,
				icon: 'fas fa-pause',
				value: _format('label_media_pause', {
					default: 'Pause media',
				}),
			};
		case 'Radio_PlayFavourite':
			return {
				name,
				icon: 'fas fa-play',
				iconColor: '--color-success',
				value: _format('label_radio_favorite_play', {
					default: 'Play favorite radio',
				}),
			};
		case 'cury_intensity': {
			const slot = action.slot || 'left';
			const slotText =
				slot === 'left'
					? _format('left_slot', { default: 'Left' }).toLowerCase()
					: _format('right_slot', { default: 'Right' }).toLowerCase();
			return {
				name,
				icon: 'fas fa-wind',
				iconColor: '--color-primary',
				value: _format('scene_action_cury_intensity_preview', {
					default: 'Set {slot} slot intensity to {intensity}%',
					values: { slot: slotText, intensity: value },
				}),
			};
		}
		case 'cury_boost': {
			const slot = action.slot || 'left';
			const slotText =
				slot === 'left'
					? _format('left_slot', { default: 'Left' }).toLowerCase()
					: _format('right_slot', { default: 'Right' }).toLowerCase();
			return {
				name,
				icon: 'fas fa-wind',
				iconColor: '--color-success',
				value: _format('scene_action_cury_boost_preview', {
					default: 'Boost {slot} slot to maximum intensity (100%)',
					values: { slot: slotText },
				}),
			};
		}
		case 'cury_stop': {
			const slot = action.slot || 'left';
			const slotText =
				slot === 'left'
					? _format('left_slot', { default: 'Left' }).toLowerCase()
					: _format('right_slot', { default: 'Right' }).toLowerCase();
			return {
				name,
				icon: 'fas fa-stop',
				iconColor: '--color-danger',
				value: _format('scene_action_cury_stop_boost_preview', {
					default: 'Stop boost {slot} slot',
					values: { slot: slotText },
				}),
			};
		}
		case 'cury_on': {
			const slot = action.slot || 'left';
			const slotText =
				slot === 'left'
					? _format('left_slot', { default: 'Left' }).toLowerCase()
					: _format('right_slot', { default: 'Right' }).toLowerCase();
			const isOn = value !== false;
			return {
				name,
				icon: 'fas fa-power-off',
				iconColor: isOn ? '--color-success' : '--color-danger',
				value: isOn
					? _format('scene_action_cury_on_preview', {
							default: 'Turn on {slot} slot',
							values: { slot: slotText },
						})
					: _format('scene_action_cury_off_preview', {
							default: 'Turn off {slot} slot',
							values: { slot: slotText },
						}),
			};
		}
		case 'cury_ambient': {
			const ambientEnabled = value?.enable || false;
			return {
				name,
				icon: 'fas fa-lightbulb',
				value: ambientEnabled
					? _format('scene_action_cury_ambient_enabled_preview', {
							default:
								'Enable ambient light with brightness {brightness}% and custom color',
							values: { brightness: value?.brightness || 0 },
						})
					: _format('scene_action_cury_ambient_disabled_preview', {
							default: 'Disable ambient light',
						}),
			};
		}

		case CAMERA_ACTION_NAMES.CAPTURE:
			return {
				name,
				icon: 'fas fa-camera',
				value: _format('local_action_camera_capture_image', { default: 'Capture Image' }),
			};
		case CAMERA_ACTION_NAMES.RECORD: {
			const base = _format('local_action_camera_start_recording', {
				default: 'Start Recording',
			});
			const durationValue = (action as any)?.duration;
			const durationText =
				durationValue != null
					? ` (${durationValue}${_format('timer_text_sec', { default: 's' })})`
					: '';
			return {
				name,
				icon: 'fas fa-video',
				value: `${base}${durationText}`,
			};
		}
		case CAMERA_ACTION_NAMES.ARM:
			return {
				name,
				icon: value === 'true' ? 'fas fa-shield' : 'fas fa-shield-halved',
				iconColor: value === 'true' ? '--color-danger' : undefined,
				value:
					value === 'true'
						? _format('camera_motion_detection_armed', { default: 'Armed' })
						: _format('camera_motion_detection_disarmed', { default: 'Disarmed' }),
			};
		case CAMERA_ACTION_NAMES.PRIVACY:
			return {
				name,
				icon: value === 'true' ? 'fas fa-video-slash' : 'fas fa-video',
				value:
					value === 'true'
						? _format('camera_privacy_mode_on', { default: 'Privacy On' })
						: _format('camera_privacy_mode_off', { default: 'Privacy Off' }),
			};
		case CAMERA_ACTION_NAMES.PLAY_SOUND:
			return {
				name,
				icon: DEFAULT_SOUND_ICON,
				value: getCameraSoundLabel(value),
			};
		case CAMERA_ACTION_NAMES.ZONE: {
			const zoneId = (action as any)?.add_info_ind ?? (action as any)?.addOnInd;
			const zoneName = getCameraZoneName(zoneId, settings[real_id(device_id)]);
			const stateText =
				value === 'true'
					? _format('camera_zone_enable', { default: 'Enabled' })
					: _format('camera_zone_disable', { default: 'Disabled' });
			return {
				name,
				icon: value === 'true' ? 'fa-duotone fa-light fa-toggle-on' : 'fa fa-toggle-off',
				value: `${stateText} – ${zoneName}`,
			};
		}

		default:
			return '';
	}
}

export function grp_action_to_label_list(action = { name: '', value: '' }) {
	const _format = get(format);
	switch (action.name) {
		case 'turn':
			const isOn = action.value === 'on';
			const isOff = action.value === 'off';

			return {
				name: 'turn',
				icon: isOff
					? 'fa fa-toggle-off'
					: isOn
						? 'fa-duotone fa-light fa-toggle-on'
						: 'fa fa-toggle-on',
				...(isOn && {
					iconStyle:
						'--fa-primary-color: rgb(0, 255, 0); --fa-secondary-color: rgba(0, 255, 0, 0.1); --fa-secondary-opacity: 1',
				}),
				...(isOff && {
					iconColor: 'rgb(255, 0, 0);',
				}),
			};
		case 'direction':
		case 'position':
			return {
				name: 'control_blinds',
				icon:
					action.value === 'open'
						? 'fa fa-blinds-raised'
						: action.value === 'close' || typeof action.value === 'number'
							? 'fa fa-blinds'
							: action.value === 'stop'
								? 'fa fa-hand'
								: false,
				iconColor:
					action.value === 'open'
						? '--color-primary'
						: action.value === 'close' || typeof action.value === 'number'
							? '--color-white'
							: action.value === 'stop'
								? '--color-danger'
								: null,
				value: _format(`execute_${action.value}`, {
					default: typeof action.value === 'number' ? `${action.value}%` : action.value,
				}),
			};
		case 'brightness':
		case 'gain':
			return {
				name: 'brightness',
				icon: 'fa fa-sun',
				value: typeof action.value === 'number' ? `${action.value}%` : String(action.value),
			};
		case 'enable':
			return {
				name: 'thermostat_enable',
				icon: action.value ? 'fa fa-thermometer-half' : 'fa fa-thermometer-empty',
				iconColor: action.value ? '--color-primary' : '--color-danger',
			};
		case 'target_t':
			return {
				name: 'thermostat_temperature',
				icon: 'fa fa-temperature-half',
				value: typeof action.value === 'number' ? `${action.value}°C` : String(action.value),
			};
		default:
			return '';
	}
}
